/** Snapping, alignment and distribution: pure geometry on canvas-px rectangles. */
import { aspectOf } from '@prism-graphics/scene-schema';
import type { Rect } from './commands.ts';

export interface SnapTargets {
  x: number[];
  y: number[];
}

export interface SnapResult {
  dx: number;
  dy: number;
  /** Guide lines to show (canvas px). */
  lines: { x: number[]; y: number[] };
}

/** Safe-area rectangles: action safe 3.5 %, graphics safe 5 % (EBU R 95), configurable. */
export function safeArea(width: number, height: number, marginPercent: number): Rect {
  const mx = (width * marginPercent) / 100;
  const my = (height * marginPercent) / 100;
  return { x: mx, y: my, width: width - 2 * mx, height: height - 2 * my };
}

/** The 4:3 centre cut of a 16:9 canvas; undefined for other shapes (a 4:3 cut of 9:16 or 1:1 means nothing). */
export function centreCut(width: number, height: number): Rect | undefined {
  if (aspectOf({ width, height }) !== '16:9') return undefined;
  const w = (height * 4) / 3;
  return { x: (width - w) / 2, y: 0, width: w, height };
}

export const edgesX = (r: Rect) => [r.x, r.x + r.width / 2, r.x + r.width];
export const edgesY = (r: Rect) => [r.y, r.y + r.height / 2, r.y + r.height];

/**
 * Snap a moving rectangle: the closest of its left/centre/right (top/middle/bottom) to any target within
 * `threshold` wins on each axis independently.
 */
export function snapRect(moving: Rect, targets: SnapTargets, threshold: number, axes: { x?: boolean; y?: boolean } = {}): SnapResult {
  const best = (mine: number[], theirs: number[]): { d: number; at: number[] } | undefined => {
    let found: { d: number; at: number[] } | undefined;
    for (const m of mine) {
      for (const t of theirs) {
        const d = t - m;
        if (Math.abs(d) > threshold) continue;
        if (!found || Math.abs(d) < Math.abs(found.d) - 1e-9) found = { d, at: [t] };
        else if (Math.abs(Math.abs(d) - Math.abs(found.d)) < 1e-9 && !found.at.includes(t)) found.at.push(t);
      }
    }
    return found;
  };
  const sx = axes.x === false ? undefined : best(edgesX(moving), targets.x);
  const sy = axes.y === false ? undefined : best(edgesY(moving), targets.y);
  return { dx: sx?.d ?? 0, dy: sy?.d ?? 0, lines: { x: sx?.at ?? [], y: sy?.at ?? [] } };
}

/** Snap a single coordinate (a resized edge). */
export function snapValue(v: number, targets: number[], threshold: number): { value: number; line?: number } {
  let best: number | undefined;
  for (const t of targets) if (Math.abs(t - v) <= threshold && (best === undefined || Math.abs(t - v) < Math.abs(best - v))) best = t;
  return best === undefined ? { value: v } : { value: best, line: best };
}

export function unionRect(rects: readonly Rect[]): Rect | undefined {
  if (rects.length === 0) return undefined;
  const x = Math.min(...rects.map((r) => r.x));
  const y = Math.min(...rects.map((r) => r.y));
  return { x, y, width: Math.max(...rects.map((r) => r.x + r.width)) - x, height: Math.max(...rects.map((r) => r.y + r.height)) - y };
}

export const intersects = (a: Rect, b: Rect) => a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;

export type Alignment = 'left' | 'hcenter' | 'right' | 'top' | 'vmiddle' | 'bottom';

/** Per-element moves that align each rectangle to `to` (the selection bounds, or the canvas for one element). */
export function align(rects: ReadonlyMap<string, Rect>, how: Alignment, to: Rect): Map<string, { x: number; y: number }> {
  const out = new Map<string, { x: number; y: number }>();
  for (const [id, r] of rects) {
    let x = 0;
    let y = 0;
    if (how === 'left') x = to.x - r.x;
    if (how === 'hcenter') x = to.x + to.width / 2 - (r.x + r.width / 2);
    if (how === 'right') x = to.x + to.width - (r.x + r.width);
    if (how === 'top') y = to.y - r.y;
    if (how === 'vmiddle') y = to.y + to.height / 2 - (r.y + r.height / 2);
    if (how === 'bottom') y = to.y + to.height - (r.y + r.height);
    out.set(id, { x: round(x), y: round(y) });
  }
  return out;
}

/** Equal gaps between three or more rectangles along an axis (the outermost two stay). */
export function distribute(rects: ReadonlyMap<string, Rect>, axis: 'x' | 'y'): Map<string, { x: number; y: number }> {
  const out = new Map<string, { x: number; y: number }>();
  const list = [...rects].sort(([, a], [, b]) => (axis === 'x' ? a.x - b.x : a.y - b.y));
  if (list.length < 3) return out;
  const size = (r: Rect) => (axis === 'x' ? r.width : r.height);
  const pos = (r: Rect) => (axis === 'x' ? r.x : r.y);
  const first = list[0]![1];
  const last = list[list.length - 1]![1];
  const span = pos(last) + size(last) - pos(first);
  const total = list.reduce((s, [, r]) => s + size(r), 0);
  const gap = (span - total) / (list.length - 1);
  let cursor = pos(first);
  for (const [id, r] of list) {
    const d = round(cursor - pos(r));
    out.set(id, axis === 'x' ? { x: d, y: 0 } : { x: 0, y: d });
    cursor += size(r) + gap;
  }
  return out;
}

const round = (n: number) => Math.round(n * 100) / 100;
